import React, { useEffect, useState } from "react";
import Select from "react-select";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import "bootstrap/dist/css/bootstrap.min.css";
import Swal from "sweetalert2";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { faChevronCircleLeft, faFileExcel } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";

const RequirementReport = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const navigate = useNavigate();

  const [ledgerOptions, setLedgerOptions] = useState([]);
  const [selectedLedgers, setSelectedLedgers] = useState([]);

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("token");

  /* ================= FETCH PROJECTS ================= */
  const fetchProjects = async () => {
    const res = await apiClient.get(`${SERVER_BASE}/area-projects`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setProjects(res.data.map(p => ({ value: p.id, label: p.projectName })));
  };

  /* ================= FETCH LEDGERS ================= */
  const fetchLedgersForProject = async (projectId) => {
    const res = await apiClient.get(
      `${SERVER_BASE}/getLedgersForProjectRequirement/${projectId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setLedgerOptions(res.data);
  };

  /* ================= GET REPORT ================= */
  const handleGetReport = async () => {
    if (!selectedProject) {
      Swal.fire("Error", "Project is required", "error");
      return;
    }

    try {
      setLoading(true);
      const res = await apiClient.post(
        `${SERVER_BASE}/getLedgerRequirementReport`,
        {
          projectId: selectedProject.value,
          ledgerIds: selectedLedgers.map(l => l.value),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setReportData(res.data || []);
    } catch {
      Swal.fire("Error", "Failed to fetch report", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ================= EXPORT TO EXCEL ================= */
  const handleExportExcel = async () => {
    if (!reportData.length) {
      Swal.fire("Warning", "No data to export", "warning");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Requirement Report");

    sheet.columns = [
      { header: "Ledger", key: "ledger", width: 40 },
      { header: "Item", key: "item", width: 30 },
      { header: "Qty", key: "qty", width: 15 },
      { header: "UOM", key: "uom", width: 15 },
    ];

    reportData.forEach(ledger => {
      // Ledger Header Row
      const headerRow = sheet.addRow({
        ledger: ledger.ledgerName,
      });
      headerRow.font = { bold: true };

      let ledgerTotal = 0;

      ledger.items.forEach(item => {
        ledgerTotal += Number(item.qty || 0);
        sheet.addRow({
          ledger: "",
          item: item.itemName,
          qty: item.qty,
          uom: item.uom,
        });
      });

      // Total Row
      const totalRow = sheet.addRow({
        item: "Total",
        qty: ledgerTotal,
      });
      totalRow.font = { bold: true };

      sheet.addRow({});
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer]),
      `Requirement_Report_${selectedProject.label}.xlsx`
    );
  };

  /* ================= EFFECTS ================= */
  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (!selectedProject) {
      setLedgerOptions([]);
      setSelectedLedgers([]);
      setReportData([]);
      return;
    }
    fetchLedgersForProject(selectedProject.value);
  }, [selectedProject]);

  return (
    <div className="formcontainer mt-4">

      {/* HEADER */}
      <div className="d-flex justify-content-between mb-3">
        <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faChevronCircleLeft} /> Back
        </button>

        <button
          className="btn btn-success btn-sm"
          onClick={handleExportExcel}
          disabled={!reportData.length}
        >
          <FontAwesomeIcon icon={faFileExcel} /> Export Excel
        </button>
      </div>

      <h5 className="mb-4">Requirement Report</h5>

      {/* FILTERS */}
      <div className="row g-3 align-items-end">
        <div className="col-md-3">
          <label className="form-label fw-semibold">
            Project <span className="text-danger">*</span>
          </label>
          <Select
            options={projects}
            value={selectedProject}
            onChange={setSelectedProject}
            placeholder="Select Project"
            isClearable
          />
        </div>

        <div className="col-md-5">
          <label className="form-label fw-semibold">Ledger (Optional)</label>
          <Select
            options={ledgerOptions}
            value={selectedLedgers}
            onChange={setSelectedLedgers}
            isMulti
            isClearable
            isDisabled={!selectedProject}
          />
        </div>

        <div className="col-md-2">
          <button
            className="btn btn-primary w-100"
            onClick={handleGetReport}
            disabled={loading}
          >
            {loading ? "Loading..." : "Get"}
          </button>
        </div>
      </div>

      {/* REPORT */}
      {reportData.map((ledger, idx) => {
        const totalQty = ledger.items.reduce(
          (sum, i) => sum + Number(i.qty || 0),
          0
        );

        return (
          <div key={ledger.ledgerId} className="mt-4">
            <div className="card-header bg-light fw-semibold">
              {idx + 1}. {ledger.ledgerName}
            </div>

            <table className="table table-bordered table-sm mb-0">
              <thead className="table-secondary">
                <tr>
                  <th>#</th>
                  <th>Item</th>
                  <th className="text-center">Qty</th>
                  <th>UOM</th>
                </tr>
              </thead>
              <tbody>
                {ledger.items.map((item, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{item.itemName}</td>
                    <td className="text-center">{item.qty}</td>
                    <td>{item.uom}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

export default RequirementReport;
